'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BudgetCalculator() {
  const t = useTranslations("tools.budgetCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Calculator state
  const [inputs, setInputs] = useState({
    salary: 80000,
    salaryPeriod: 'y',
    pension: 0,
    pensionPeriod: 'y',
    investment: 1000,
    investmentPeriod: 'y',
    otherIncome: 2000,
    otherIncomePeriod: 'y',
    taxRate: 28,
    mortgage: 0,
    mortgagePeriod: 'm',
    propertyTax: 0,
    propertyTaxPeriod: 'y',
    rental: 1400,
    rentalPeriod: 'm',
    houseInsurance: 200,
    houseInsurancePeriod: 'y',
    utilities: 250,
    utilitiesPeriod: 'm',
    autoLoan: 250,
    autoLoanPeriod: 'm',
    autoInsurance: 700,
    autoInsurancePeriod: 'y',
    gasoline: 100,
    gasolinePeriod: 'm',
    creditCard: 0,
    creditCardPeriod: 'm',
    studentLoan: 250,
    studentLoanPeriod: 'm',
    food: 400,
    foodPeriod: 'm',
    clothing: 100,
    clothingPeriod: 'm',
    mealsOut: 200,
    mealsOutPeriod: 'm',
    medicalInsurance: 0,
    medicalInsurancePeriod: 'm',
    medicalSpending: 200,
    medicalSpendingPeriod: 'm',
    retirement: 10000,
    retirementPeriod: 'y',
    savings: 0,
    savingsPeriod: 'm',
  });

  const [results, setResults] = useState<any>(null);

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Colors
  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  // Styles
  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolHeader: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '1.5rem',
      textAlign: 'center' as const,
      borderRadius: '1rem 1rem 0 0',
      marginBottom: '2rem',
    },
    toolTitle: getResponsiveStyle(
      { margin: 0, fontWeight: 700, color: 'white' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    sectionTitle: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        padding: '0.75rem 1rem',
        margin: '2rem 0 1rem 0',
        fontWeight: 600,
        borderRadius: '0.5rem',
      },
      { fontSize: '1rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' }
    ),
    subsectionTitle: getResponsiveStyle(
      {
        background: '#e0e7ff',
        color: '#3730a3',
        padding: '0.5rem 1rem',
        margin: '1.5rem 0 1rem 0',
        fontWeight: 600,
        borderRadius: '0.5rem',
      },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    formRow: getResponsiveStyle(
      {
        display: 'grid',
        gap: '1rem',
        alignItems: 'center',
        marginBottom: '1rem',
      },
      { gridTemplateColumns: '1fr', padding: '0 0.5rem' },
      { gridTemplateColumns: '1fr 1fr auto', padding: '0 1rem' },
      { gridTemplateColumns: '1fr 1fr auto', padding: '0 1rem' }
    ),
    label: getResponsiveStyle(
      { color: colors.textPrimary, fontWeight: 500 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    input: {
      padding: '0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s',
      width: '100%',
      boxSizing: 'border-box' as const,
    },
    select: {
      padding: '0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      background: 'white',
      cursor: 'pointer',
      width: '100%',
      boxSizing: 'border-box' as const,
    },
    hintText: getResponsiveStyle(
      { color: '#6b7280', marginTop: '-0.5rem' },
      { fontSize: '0.8rem', gridColumn: '1' },
      { fontSize: '0.85rem', gridColumn: '2 / 4' },
      { fontSize: '0.85rem', gridColumn: '2 / 4' }
    ),
    calculateButton: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        color: 'white',
        border: 'none',
        fontWeight: 600,
        borderRadius: '0.5rem',
        cursor: 'pointer',
        transition: 'all 0.3s',
        display: 'block',
        margin: '2rem auto 0',
        boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
        width: '100%',
        maxWidth: '300px',
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.05rem' },
      { padding: '1rem 3rem', fontSize: '1.1rem' }
    ),
    resultsContainer: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderRadius: '1rem',
        border: '2px solid #86efac',
        marginTop: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { color: '#15803d', margin: '0 0 1.5rem 0', textAlign: 'center' as const },
      { fontSize: '1.25rem' },
      { fontSize: '1.4rem' },
      { fontSize: '1.5rem' }
    ),
    resultGrid: getResponsiveStyle(
      {
        display: 'grid',
        gap: '1rem',
        marginBottom: '2rem',
      },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
    ),
    resultCard: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    resultCardTitle: getResponsiveStyle(
      { color: colors.textPrimary, margin: '0 0 0.5rem 0', fontWeight: 600 },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.9rem' }
    ),
    resultValue: getResponsiveStyle(
      { fontWeight: 700, color: '#2563eb' },
      { fontSize: '1.5rem' },
      { fontSize: '1.65rem' },
      { fontSize: '1.75rem' }
    ),
    breakdownTable: {
      background: 'white',
      borderRadius: '0.75rem',
      overflow: 'hidden',
      marginTop: '1.5rem',
      overflowX: 'auto' as const,
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      minWidth: '600px',
    },
    th: getResponsiveStyle(
      {
        background: '#2563eb',
        color: 'white',
        padding: '1rem',
        textAlign: 'left' as const,
        fontWeight: 600,
      },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      { fontSize: '1rem' }
    ),
    td: getResponsiveStyle(
      {
        padding: '0.75rem 1rem',
        borderBottom: '1px solid #e5e7eb',
      },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' }
    ),
    categoryRow: {
      background: '#f3f4f6',
      fontWeight: 600,
    },
    totalRow: getResponsiveStyle(
      {
        background: '#dbeafe',
        fontWeight: 700,
      },
      { fontSize: '0.9rem' },
      { fontSize: '1rem' },
      { fontSize: '1.1rem' }
    ),
    chartContainer: {
      marginTop: '2rem',
      textAlign: 'center' as const,
    },
    chartBars: getResponsiveStyle(
      {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-around',
        margin: '1rem 0',
        padding: '1rem',
        background: 'white',
        borderRadius: '0.75rem',
      },
      { height: '150px', flexWrap: 'wrap' as const },
      { height: '180px' },
      { height: '200px' }
    ),
    chartBar: {
      flex: 1,
      margin: '0 0.5rem',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'flex-end',
      minWidth: '60px',
    },
    bar: {
      width: '100%',
      background: 'linear-gradient(to top, #2563eb, #7c3aed)',
      borderRadius: '0.5rem 0.5rem 0 0',
      transition: 'all 0.3s',
      position: 'relative' as const,
    },
    barLabel: getResponsiveStyle(
      { marginTop: '0.5rem', color: colors.textPrimary, fontWeight: 600 },
      { fontSize: '0.7rem' },
      { fontSize: '0.8rem' },
      { fontSize: '0.85rem' }
    ),
    barValue: getResponsiveStyle(
      {
        position: 'absolute' as const,
        top: '-1.5rem',
        left: '50%',
        transform: 'translateX(-50%)',
        color: '#6b7280',
        whiteSpace: 'nowrap' as const,
      },
      { fontSize: '0.65rem' },
      { fontSize: '0.7rem' },
      { fontSize: '0.75rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', borderRadius: '1.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.4rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, marginLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.15rem' },
      { fontSize: '1.25rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary },
      { fontSize: '0.95rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.125rem' }
    ),
    reference: {
      marginBottom: '0.75rem',
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      fontSize: '0.95rem',
      wordBreak: 'break-word' as const,
    },
  };

  // Helper function to convert to annual
  const toAnnual = (value: number, period: string) => {
    return period === 'm' ? value * 12 : value;
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return '$' + Math.abs(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Format percent
  const formatPercent = (value: number) => {
    return value.toFixed(1) + '%';
  };

  // Handle input change
  const handleInputChange = (field: string, value: string | number) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  // Calculate budget
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const grossIncome = toAnnual(inputs.salary, inputs.salaryPeriod) +
                       toAnnual(inputs.pension, inputs.pensionPeriod) +
                       toAnnual(inputs.investment, inputs.investmentPeriod) +
                       toAnnual(inputs.otherIncome, inputs.otherIncomePeriod);

    const retirement = toAnnual(inputs.retirement, inputs.retirementPeriod);
    const taxableIncome = grossIncome - retirement;
    const taxes = taxableIncome * (inputs.taxRate / 100);
    const netIncome = grossIncome - taxes;

    const housing = toAnnual(inputs.mortgage, inputs.mortgagePeriod) +
                   toAnnual(inputs.propertyTax, inputs.propertyTaxPeriod) +
                   toAnnual(inputs.rental, inputs.rentalPeriod) +
                   toAnnual(inputs.houseInsurance, inputs.houseInsurancePeriod) +
                   toAnnual(inputs.utilities, inputs.utilitiesPeriod);

    const transportation = toAnnual(inputs.autoLoan, inputs.autoLoanPeriod) +
                          toAnnual(inputs.autoInsurance, inputs.autoInsurancePeriod) +
                          toAnnual(inputs.gasoline, inputs.gasolinePeriod);

    const debt = toAnnual(inputs.creditCard, inputs.creditCardPeriod) +
                toAnnual(inputs.studentLoan, inputs.studentLoanPeriod);

    const living = toAnnual(inputs.food, inputs.foodPeriod) +
                  toAnnual(inputs.clothing, inputs.clothingPeriod) +
                  toAnnual(inputs.mealsOut, inputs.mealsOutPeriod);

    const healthcare = toAnnual(inputs.medicalInsurance, inputs.medicalInsurancePeriod) +
                      toAnnual(inputs.medicalSpending, inputs.medicalSpendingPeriod);

    const savingsExp = toAnnual(inputs.savings, inputs.savingsPeriod) + retirement;

    const totalExpenses = housing + transportation + debt + living + healthcare + savingsExp;
    const surplus = netIncome - totalExpenses;

    const categories = [
      { name: t('categoryHousing'), annual: housing },
      { name: t('categoryTransportation'), annual: transportation },
      { name: t('categoryDebt'), annual: debt },
      { name: t('categoryLiving'), annual: living },
      { name: t('categoryHealthcare'), annual: healthcare },
      { name: t('categorySavings'), annual: savingsExp }
    ];

    setResults({
      netIncome,
      totalExpenses,
      surplus,
      categories,
    });

    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolHeader}>
          <h2 style={styles.toolTitle}>{t('toolTitle')}</h2>
        </div>

        <form onSubmit={handleCalculate} style={{ padding: screenSize === 'mobile' ? '0' : '0 1rem' }}>
          {/* Income Section */}
          <div style={styles.sectionTitle}>{t('sectionIncome')}</div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelSalary')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.salary}
              onChange={(e) => handleInputChange('salary', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.salaryPeriod}
              onChange={(e) => handleInputChange('salaryPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelPension')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.pension}
              onChange={(e) => handleInputChange('pension', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.pensionPeriod}
              onChange={(e) => handleInputChange('pensionPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelInvestment')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.investment}
              onChange={(e) => handleInputChange('investment', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.investmentPeriod}
              onChange={(e) => handleInputChange('investmentPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>
          <div style={styles.hintText}>{t('hintInvestment')}</div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelOtherIncome')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.otherIncome}
              onChange={(e) => handleInputChange('otherIncome', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.otherIncomePeriod}
              onChange={(e) => handleInputChange('otherIncomePeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>
          <div style={styles.hintText}>{t('hintOtherIncome')}</div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelTaxRate')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.taxRate}
              onChange={(e) => handleInputChange('taxRate', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
              max="100"
            />
            <span></span>
          </div>
          <div style={styles.hintText}>{t('hintTaxRate')}</div>

          {/* Expenses Section */}
          <div style={styles.sectionTitle}>{t('sectionExpenses')}</div>

          {/* Housing & Utilities */}
          <div style={styles.subsectionTitle}>{t('subsectionHousing')}</div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelMortgage')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.mortgage}
              onChange={(e) => handleInputChange('mortgage', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.mortgagePeriod}
              onChange={(e) => handleInputChange('mortgagePeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelPropertyTax')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.propertyTax}
              onChange={(e) => handleInputChange('propertyTax', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.propertyTaxPeriod}
              onChange={(e) => handleInputChange('propertyTaxPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelRental')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.rental}
              onChange={(e) => handleInputChange('rental', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.rentalPeriod}
              onChange={(e) => handleInputChange('rentalPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelHouseInsurance')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.houseInsurance}
              onChange={(e) => handleInputChange('houseInsurance', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.houseInsurancePeriod}
              onChange={(e) => handleInputChange('houseInsurancePeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>
          <div style={styles.hintText}>{t('hintHouseInsurance')}</div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelUtilities')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.utilities}
              onChange={(e) => handleInputChange('utilities', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.utilitiesPeriod}
              onChange={(e) => handleInputChange('utilitiesPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>
          <div style={styles.hintText}>{t('hintUtilities')}</div>

          {/* Transportation */}
          <div style={styles.subsectionTitle}>{t('subsectionTransportation')}</div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelAutoLoan')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.autoLoan}
              onChange={(e) => handleInputChange('autoLoan', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.autoLoanPeriod}
              onChange={(e) => handleInputChange('autoLoanPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelAutoInsurance')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.autoInsurance}
              onChange={(e) => handleInputChange('autoInsurance', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.autoInsurancePeriod}
              onChange={(e) => handleInputChange('autoInsurancePeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelGasoline')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.gasoline}
              onChange={(e) => handleInputChange('gasoline', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.gasolinePeriod}
              onChange={(e) => handleInputChange('gasolinePeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          {/* Other Debt */}
          <div style={styles.subsectionTitle}>{t('subsectionDebt')}</div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelCreditCard')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.creditCard}
              onChange={(e) => handleInputChange('creditCard', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.creditCardPeriod}
              onChange={(e) => handleInputChange('creditCardPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelStudentLoan')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.studentLoan}
              onChange={(e) => handleInputChange('studentLoan', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.studentLoanPeriod}
              onChange={(e) => handleInputChange('studentLoanPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          {/* Living Expenses */}
          <div style={styles.subsectionTitle}>{t('subsectionLiving')}</div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelFood')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.food}
              onChange={(e) => handleInputChange('food', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.foodPeriod}
              onChange={(e) => handleInputChange('foodPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelClothing')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.clothing}
              onChange={(e) => handleInputChange('clothing', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.clothingPeriod}
              onChange={(e) => handleInputChange('clothingPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelMealsOut')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.mealsOut}
              onChange={(e) => handleInputChange('mealsOut', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.mealsOutPeriod}
              onChange={(e) => handleInputChange('mealsOutPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          {/* Healthcare */}
          <div style={styles.subsectionTitle}>{t('subsectionHealthcare')}</div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelMedicalInsurance')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.medicalInsurance}
              onChange={(e) => handleInputChange('medicalInsurance', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.medicalInsurancePeriod}
              onChange={(e) => handleInputChange('medicalInsurancePeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelMedicalSpending')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.medicalSpending}
              onChange={(e) => handleInputChange('medicalSpending', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.medicalSpendingPeriod}
              onChange={(e) => handleInputChange('medicalSpendingPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          {/* Savings & Investments */}
          <div style={styles.subsectionTitle}>{t('subsectionSavings')}</div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelRetirement')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.retirement}
              onChange={(e) => handleInputChange('retirement', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.retirementPeriod}
              onChange={(e) => handleInputChange('retirementPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>
          <div style={styles.hintText}>{t('hintRetirement')}</div>

          <div style={styles.formRow}>
            <label style={styles.label}>{t('labelSavings')}</label>
            <input
              type="number"
              style={styles.input}
              value={inputs.savings}
              onChange={(e) => handleInputChange('savings', parseFloat(e.target.value) || 0)}
              step="0.01"
              min="0"
            />
            <select
              style={styles.select}
              value={inputs.savingsPeriod}
              onChange={(e) => handleInputChange('savingsPeriod', e.target.value)}
            >
              <option value="m">{t('periodMonth')}</option>
              <option value="y">{t('periodYear')}</option>
            </select>
          </div>

          <button type="submit" style={styles.calculateButton}>
            {t('calculateButton')}
          </button>
        </form>

        {/* Results */}
        {results && (
          <div id="results" style={styles.resultsContainer}>
            <h3 style={styles.resultsTitle}>{t('resultsTitle')}</h3>

            <div style={styles.resultGrid}>
              <div style={styles.resultCard}>
                <h4 style={styles.resultCardTitle}>{t('resultNetIncome')}</h4>
                <div style={styles.resultValue}>{formatCurrency(results.netIncome)}</div>
              </div>
              <div style={styles.resultCard}>
                <h4 style={styles.resultCardTitle}>{t('resultTotalExpenses')}</h4>
                <div style={styles.resultValue}>{formatCurrency(results.totalExpenses)}</div>
              </div>
              <div style={{...styles.resultCard, ...(results.surplus >= 0 ? {} : {})}}>
                <h4 style={styles.resultCardTitle}>{t('resultSurplus')}</h4>
                <div style={{...styles.resultValue, color: results.surplus >= 0 ? '#16a34a' : '#dc2626'}}>
                  {(results.surplus >= 0 ? '+' : '-') + formatCurrency(results.surplus)}
                </div>
              </div>
              <div style={styles.resultCard}>
                <h4 style={styles.resultCardTitle}>{t('resultMonthlySurplus')}</h4>
                <div style={{...styles.resultValue, color: results.surplus >= 0 ? '#16a34a' : '#dc2626'}}>
                  {(results.surplus >= 0 ? '+' : '-') + formatCurrency(results.surplus / 12)}
                </div>
              </div>
            </div>

            <div style={styles.breakdownTable}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{t('tableCategory')}</th>
                    <th style={{...styles.th, textAlign: 'right'}}>{t('tableMonthly')}</th>
                    <th style={{...styles.th, textAlign: 'right'}}>{t('tableAnnual')}</th>
                    <th style={{...styles.th, textAlign: 'right'}}>{t('tablePercent')}</th>
                  </tr>
                </thead>
                <tbody>
                  {results.categories.map((cat: any, idx: number) => (
                    <tr key={idx}>
                      <td style={styles.td}>{cat.name}</td>
                      <td style={{...styles.td, textAlign: 'right'}}>{formatCurrency(cat.annual / 12)}</td>
                      <td style={{...styles.td, textAlign: 'right'}}>{formatCurrency(cat.annual)}</td>
                      <td style={{...styles.td, textAlign: 'right'}}>
                        {formatPercent(results.netIncome > 0 ? (cat.annual / results.netIncome * 100) : 0)}
                      </td>
                    </tr>
                  ))}
                  <tr style={styles.totalRow}>
                    <td style={styles.td}>{t('tableTotal')}</td>
                    <td style={{...styles.td, textAlign: 'right'}}>{formatCurrency(results.totalExpenses / 12)}</td>
                    <td style={{...styles.td, textAlign: 'right'}}>{formatCurrency(results.totalExpenses)}</td>
                    <td style={{...styles.td, textAlign: 'right'}}>
                      {formatPercent(results.netIncome > 0 ? (results.totalExpenses / results.netIncome * 100) : 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={styles.chartContainer}>
              <h4 style={{...styles.h3, marginTop: 0}}>{t('chartTitle')}</h4>
              <div style={styles.chartBars}>
                {results.categories.map((cat: any, idx: number) => {
                  const maxValue = Math.max(...results.categories.map((c: any) => c.annual));
                  const height = maxValue > 0 ? (cat.annual / maxValue * 100) : 0;

                  return (
                    <div key={idx} style={styles.chartBar}>
                      <div style={{...styles.bar, height: `${height}%`}}>
                        <div style={styles.barValue}>{formatCurrency(cat.annual)}</div>
                      </div>
                      <div style={styles.barLabel}>{cat.name.split(' ')[0]}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introPara1')}</p>
        <p style={styles.paragraph}>{t('introPara2')}</p>
        <p style={styles.paragraph}>{t('introPara3')}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep1')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep2')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep3')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep4')}</li>
          <li style={{marginBottom: '0.75rem'}}>{t('howToStep5')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <p style={styles.paragraph}>{t('insightsPara1')}</p>

        <h3 style={styles.h3}>{t('insightsSubtitle1')}</h3>
        <p style={styles.paragraph}>{t('insightsContent1')}</p>

        <h3 style={styles.h3}>{t('insightsSubtitle2')}</h3>
        <p style={styles.paragraph}>{t('insightsContent2')}</p>

        <h3 style={styles.h3}>{t('insightsSubtitle3')}</h3>
        <p style={styles.paragraph}>{t('insightsContent3')}</p>
      </section>

      {/* Details */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>

        <h3 style={styles.h3}>{t('detailsSubtitle1')}</h3>
        <p style={styles.paragraph}>{t('detailsContent1')}</p>

        <h3 style={styles.h3}>{t('detailsSubtitle2')}</h3>
        <p style={styles.paragraph}>{t('detailsContent2')}</p>

        <h3 style={styles.h3}>{t('detailsSubtitle3')}</h3>
        <p style={styles.paragraph}>{t('detailsContent3')}</p>

        <h3 style={styles.h3}>{t('detailsSubtitle4')}</h3>
        <p style={styles.paragraph}>{t('detailsContent4')}</p>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>

        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq6Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq6Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <div style={styles.reference}>
          <a href="https://www.marshmallowchallenge.com/blog/the-ultimate-budget-calculators-for-2025-stay-organised-stay-stress-free/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            [1] The Ultimate Budget Calculators for 2025 - Marshmallow Challenge
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://superagi.com/10-ai-budget-planning-tools-to-revolutionize-your-personal-finance-in-2025-a-beginners-guide/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            [2] 10 AI Budget Planning Tools to Revolutionize Your Personal Finance in 2025 - SuperAGI
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.businessinsider.com/personal-finance/banking/best-budgeting-apps" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            [3] Best Budgeting Apps for May 2025 - Business Insider
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.bigtime.net/blogs/project-budgeting-software/" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            [4] 10 Top Project Budgeting Software to Choose in 2025 - BigTime
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.nerdwallet.com/finance/learn/best-budget-apps" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            [5] The Best Budget Apps for 2025 - NerdWallet
          </a>
        </div>
        <div style={styles.reference}>
          <a href="https://www.schwabmoneywise.com/tools-resources/monthly-budget-planner" target="_blank" rel="noopener noreferrer" style={styles.referenceLink}>
            [6] Monthly Budget Planner - Schwab Moneywise
          </a>
        </div>
      </section>
    </div>
  );
}